import { useCallback, useEffect, useRef } from 'react';

export const COMPACT_HEIGHT = 64;

/** Expanded-height fallback used before the responsive `--hero-expanded` sizer
 *  has been measured (matches the previous fixed hero height). */
const DEFAULT_EXPANDED = 260;

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/**
 * How to lock the brand hero (opt-in): `'full'` = expanded, `'compact'` = the
 * 64px bar, or a specific pixel height (rendered as a frozen mid-collapse).
 */
export type HeroLock = 'full' | 'compact' | number;

/** Scroll position → collapse progress (0 = expanded, 1 = compact). */
export function progressFor(scrollTop: number, distance: number): number {
  return distance <= 0 ? 1 : clamp01(scrollTop / distance);
}

/**
 * Fixed `--collapse-progress` for a locked hero, measured against the given
 * expanded height: `'full'` → 0, `'compact'` → 1, and a numeric px height →
 * the progress that renders the hero at exactly that height (a frozen
 * mid-collapse, clamped to `[COMPACT_HEIGHT, expanded]`).
 */
export function lockProgress(lock: HeroLock, expanded: number): number {
  if (lock === 'full') return 0;
  if (lock === 'compact') return 1;
  const h = Math.max(COMPACT_HEIGHT, Math.min(lock, expanded));
  return progressFor(expanded - h, expanded - COMPACT_HEIGHT);
}

/**
 * Drives the collapsing brand hero from the form sheet's OWN scroll container
 * (not window scroll) so collapse is deterministic on iOS Safari and immune to
 * URL-bar resize jank.
 *
 * The size math lives in CSS (see BrandHero.css): this hook only writes a single
 * unitless `--collapse-progress` (0 = expanded → 1 = compact) on the card
 * (`cardRef`) imperatively per rAF-throttled scroll frame — no React re-render.
 * The responsive expanded height is measured off `sizerRef` (whose height *is*
 * `var(--hero-expanded)`) via a ResizeObserver, so the scroll→progress mapping
 * and lock math stay correct across dvh changes (URL bar, rotation).
 *
 * Pass `lock` to freeze the hero instead of collapsing on scroll.
 */
export function useCollapsingHeader(lock?: HeroLock) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const sizerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef(0);
  const distanceRef = useRef(DEFAULT_EXPANDED - COMPACT_HEIGHT);

  const setProgress = useCallback((p: number) => {
    cardRef.current?.style.setProperty('--collapse-progress', String(p));
  }, []);

  // Recompute the collapse distance from the measured expanded height and write
  // the progress the current state implies (locked value, or the live scroll).
  const apply = useCallback(() => {
    const expanded = sizerRef.current?.offsetHeight || DEFAULT_EXPANDED;
    distanceRef.current = Math.max(1, expanded - COMPACT_HEIGHT);
    setProgress(
      lock != null
        ? lockProgress(lock, expanded)
        : progressFor(scrollRef.current?.scrollTop ?? 0, distanceRef.current)
    );
  }, [lock, setProgress]);

  // Apply on mount + whenever the lock changes, and keep in sync with device
  // height (dvh) changes via ResizeObserver on the sizer.
  useEffect(() => {
    apply();
    const el = sizerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [apply]);

  const onScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (lock != null) return; // locked: scroll no longer drives collapse
      const scrollTop = e.currentTarget.scrollTop;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() =>
        setProgress(progressFor(scrollTop, distanceRef.current))
      );
    },
    [lock, setProgress]
  );

  return { scrollRef, cardRef, sizerRef, onScroll };
}
